import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  operationsApi,
  type AdminUserListParams,
  type CreateAdminUserInput,
  type UpdateAdminUserInput,
  type CreateRoleInput,
  type UpdateRoleInput,
  type TaskListParams,
  type CreateTaskInput,
  type UpdateTaskInput,
  type ShiftListParams,
  type CreateShiftInput,
  type IncidentListParams,
  type CreateIncidentInput,
  type MessageListParams,
  type SendMessageInput,
} from "@/lib/admin-api/operations";

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

const KEYS = {
  adminUsers: (params: AdminUserListParams) =>
    ["admin-operations-users", params] as const,
  roles: ["admin-operations-roles"] as const,
  tasks: (params: TaskListParams) =>
    ["admin-operations-tasks", params] as const,
  shifts: (params: ShiftListParams) =>
    ["admin-operations-shifts", params] as const,
  performance: (period?: string) =>
    ["admin-operations-performance", period] as const,
  sla: ["admin-operations-sla"] as const,
  incidents: (params: IncidentListParams) =>
    ["admin-operations-incidents", params] as const,
  messages: (params: MessageListParams) =>
    ["admin-operations-messages", params] as const,
} as const;

// ---------------------------------------------------------------------------
// Admin Users
// ---------------------------------------------------------------------------

export function useAdminUsers(params: AdminUserListParams = {}) {
  return useQuery({
    queryKey: KEYS.adminUsers(params),
    queryFn: () => operationsApi.adminUsers.list(params),
    staleTime: 30_000,
  });
}

export function useCreateAdminUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateAdminUserInput) =>
      operationsApi.adminUsers.create(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-operations-users"] });
    },
  });
}

export function useUpdateAdminUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { readonly id: string; readonly input: UpdateAdminUserInput }) =>
      operationsApi.adminUsers.update(id, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-operations-users"] });
    },
  });
}

export function useDeactivateAdminUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => operationsApi.adminUsers.deactivate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-operations-users"] });
    },
  });
}

export function useResetAdminPassword() {
  return useMutation({
    mutationFn: (id: string) => operationsApi.adminUsers.resetPassword(id),
  });
}

// ---------------------------------------------------------------------------
// Roles
// ---------------------------------------------------------------------------

export function useRoles() {
  return useQuery({
    queryKey: KEYS.roles,
    queryFn: () => operationsApi.roles.list(),
    staleTime: 60_000,
  });
}

export function useCreateRole() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateRoleInput) => operationsApi.roles.create(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEYS.roles });
    },
  });
}

export function useUpdateRole() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { readonly id: string; readonly input: UpdateRoleInput }) =>
      operationsApi.roles.update(id, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEYS.roles });
    },
  });
}

export function useDeleteRole() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => operationsApi.roles.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEYS.roles });
    },
  });
}

// ---------------------------------------------------------------------------
// Tasks
// ---------------------------------------------------------------------------

export function useTasks(params: TaskListParams = {}) {
  return useQuery({
    queryKey: KEYS.tasks(params),
    queryFn: () => operationsApi.tasks.list(params),
    staleTime: 30_000,
  });
}

export function useCreateTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateTaskInput) => operationsApi.tasks.create(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-operations-tasks"] });
    },
  });
}

export function useUpdateTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { readonly id: string; readonly patch: UpdateTaskInput }) =>
      operationsApi.tasks.update(id, patch),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-operations-tasks"] });
    },
  });
}

// ---------------------------------------------------------------------------
// Shifts
// ---------------------------------------------------------------------------

export function useShifts(params: ShiftListParams = {}) {
  return useQuery({
    queryKey: KEYS.shifts(params),
    queryFn: () => operationsApi.shifts.list(params),
    staleTime: 30_000,
  });
}

export function useCreateShift() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateShiftInput) => operationsApi.shifts.create(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-operations-shifts"] });
    },
  });
}

// ---------------------------------------------------------------------------
// Agent Performance
// ---------------------------------------------------------------------------

export function useAgentPerformance(period?: string) {
  return useQuery({
    queryKey: KEYS.performance(period),
    queryFn: () => operationsApi.performance.get(period),
    staleTime: 60_000,
  });
}

// ---------------------------------------------------------------------------
// SLA
// ---------------------------------------------------------------------------

export function useSLA() {
  return useQuery({
    queryKey: KEYS.sla,
    queryFn: () => operationsApi.sla.get(),
    staleTime: 60_000,
  });
}

export function useUpdateSLAConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, target_minutes }: { readonly id: string; readonly target_minutes: number }) =>
      operationsApi.sla.update(id, target_minutes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEYS.sla });
    },
  });
}

// ---------------------------------------------------------------------------
// Incidents
// ---------------------------------------------------------------------------

export function useIncidents(params: IncidentListParams = {}) {
  return useQuery({
    queryKey: KEYS.incidents(params),
    queryFn: () => operationsApi.incidents.list(params),
    staleTime: 30_000,
  });
}

export function useCreateIncident() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateIncidentInput) => operationsApi.incidents.create(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-operations-incidents"] });
    },
  });
}

export function useResolveIncident() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => operationsApi.incidents.resolve(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-operations-incidents"] });
    },
  });
}

// ---------------------------------------------------------------------------
// Messages
// ---------------------------------------------------------------------------

export function useMessages(params: MessageListParams = {}) {
  return useQuery({
    queryKey: KEYS.messages(params),
    queryFn: () => operationsApi.messages.list(params),
    staleTime: 20_000,
  });
}

export function useSendMessage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: SendMessageInput) => operationsApi.messages.send(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-operations-messages"] });
    },
  });
}

export function useMarkMessageRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => operationsApi.messages.markRead(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-operations-messages"] });
    },
  });
}
